/* aiperth.co — application (ES module)
   Cloudflare Pages + Functions deploy path.
   Content + interactive features ported from the original one-shot demo.
*/
import "./router.js";
import { initMobileNav } from "./mobile-nav.js";

/* ============ aiperth.co — app logic + content ============ */
"use strict";

/* ---------- helpers ---------- */
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function toast(msg) {
  const t = $("#toast");
  t.textContent = msg;
  t.classList.add("show");
  clearTimeout(toast._t);
  toast._t = setTimeout(() => t.classList.remove("show"), 2200);
}

/* ---------- navigation ---------- */
let stopDictation = () => {};   // reassigned by the voice-input module if speech is supported

function goTo(section) {
  // Hash router owns navigation (shareable #/section URLs)
  if (typeof window.__aiadvNavigate === "function") window.__aiadvNavigate(section);
  else {
    stopDictation(section);
    $$(".page").forEach(p => p.classList.remove("active"));
    const page = $("#page-" + section);
    (page || $("#page-home")).classList.add("active");
    $$(".nav-btn").forEach(b => b.classList.toggle("active", b.dataset.nav === section));
    window.scrollTo({ top: 0 });
  }
}
// Expose dictation stop for the router
window.__aiadvStopDictation = (section) => stopDictation(section);
// data-nav clicks handled by router.js

/* ============================================================
   HOME cards
============================================================ */
const homeCards = [
  { icon: "🧭", nav: "quiz", title: "Find your starting point", body: "A 2-minute quiz that looks at your business and comfort level, then hands you a specific first project.", link: "Take the quiz" },
  { icon: "📖", nav: "basics", title: "AI Basics", body: "What AI actually is, how it works (in plain English), myths vs. reality, a glossary, and honest FAQs.", link: "Learn the basics" },
  { icon: "💡", nav: "usecases", title: "Use cases for your industry", body: "Concrete examples for restaurants, trades, retail, salons, professional services, and more.", link: "See examples" },
  { icon: "🪜", nav: "walkthroughs", title: "Step-by-step walkthroughs", body: "Recipes you can follow today: your first AI email, an FAQ chatbot, a month of social posts, and more.", link: "Follow a walkthrough" },
  { icon: "📋", nav: "prompts", title: "Copy-paste prompt library", body: "Proven templates for marketing, customer service, admin, sales, and hiring. Fill in the blanks and go.", link: "Browse prompts" },
  { icon: "✨", nav: "booster", title: "Prompt Booster", body: "Type your rough idea in plain English — free instant boost, or AI Boost with Grok for $2/month.", link: "Boost a prompt" },
  { icon: "💰", nav: "tools", title: "Tools, costs & ROI", body: "Which tools are worth paying for, typical prices, and a calculator that shows what your hours are worth.", link: "See tools & costs" },
  { icon: "🛡️", nav: "safety", title: "Staying safe", body: "The real risks — privacy, wrong answers, over-trust — and the simple rules that keep you out of trouble.", link: "Read the rules" },
  { icon: "🗓️", nav: "plan", title: "Your 30-day plan", body: "A week-by-week checklist that takes you from 'never used AI' to 'AI handles my routine work'.", link: "Start the plan" },
];
$("#homeCards").innerHTML = homeCards.map(c => `
  <button class="feature-card" data-nav="${c.nav}">
    <span class="icon">${c.icon}</span>
    <h3>${esc(c.title)}</h3>
    <p>${esc(c.body)}</p>
    <span class="link">${esc(c.link)} →</span>
  </button>`).join("");

/* ============================================================
   BASICS
============================================================ */
const explainers = [
  {
    icon: "🤖", title: "What is AI, really?",
    body: [
      "The AI everyone is talking about is mostly one thing: software that has read an enormous amount of text (and images) and learned the patterns in it. Because of that, it can write, summarize, answer questions, and brainstorm at a surprisingly high level.",
      "You use it through a chat window: you type a request in normal English, it types back. No coding, no setup, no IT department."
    ],
    analogy: "Think of it as a brilliant, endlessly patient temp worker who has read the whole internet — but started at your company this morning. Great general skills; knows nothing about your business until you tell it."
  },
  {
    icon: "💬", title: "What's a 'prompt'?",
    body: [
      "A prompt is simply what you type to the AI. The more context you give — who you are, who the audience is, what tone you want, what a good result looks like — the better the answer.",
      "This is 90% of the skill. 'Write a promo email' gets a generic result. 'Write a friendly 100-word email to past customers of my plumbing business offering 10% off spring maintenance' gets something you can actually send."
    ],
    analogy: "Brief the AI the way you'd brief a new employee: the more of the picture you give them, the less you have to fix afterwards."
  },
  {
    icon: "🎯", title: "Why it sometimes gets things wrong",
    body: [
      "AI predicts plausible-sounding text. Most of the time that's also correct text — but when it doesn't know something, it can confidently make things up. This is called a 'hallucination'.",
      "That's why the golden rule is: AI drafts, you decide. Use it for first drafts and ideas; verify any fact, number, price, or legal claim before it goes out the door."
    ],
    analogy: "Treat its output like work from a confident new hire: usually good, occasionally very wrong, always worth a quick read before it reaches a customer."
  },
  {
    icon: "🔌", title: "The three ways businesses use AI",
    body: [
      "1) Assistant — you chat with a general tool (ChatGPT, Claude, Gemini, Copilot) to draft, summarize, and brainstorm. This is where 90% of small businesses should start.",
      "2) Built-in — AI features inside software you already use: your email, accounting, booking, or point-of-sale system. Often free, already there — just switch them on.",
      "3) Automation — AI runs a task without you, like a chatbot answering website FAQs at 2am or a tool that drafts replies to every review. Worth it once you know what you want automated."
    ],
    analogy: "Start with the assistant. It's the cheapest, safest way to learn what AI can do before you automate anything."
  },
];

const myths = [
  { myth: "AI is only for tech companies and big corporations.", truth: "The biggest wins per dollar go to small businesses — you have the same repetitive writing and admin work, but no staff to delegate it to. A $20/month assistant is the cheapest 'hire' you will ever make." },
  { myth: "I need to learn to code or take a course first.", truth: "If you can write a text message, you can use AI. The whole interface is a chat box. The only skill worth practicing is describing what you want clearly." },
  { myth: "AI will replace me or my staff.", truth: "In a small business, AI replaces tasks, not people — the first drafts, the repetitive replies, the note-taking. You and your team keep the judgment, the relationships, and the final say." },
  { myth: "AI is too expensive.", truth: "The major assistants have genuinely useful free tiers. The paid versions are $20–30/month — less than one hour of most people's time. Big custom AI projects are expensive; you don't need one." },
  { myth: "Whatever I type gets shared with the world.", truth: "Reputable tools don't leak your chats to other users. But do check the settings: on business plans (and often via a toggle) you can opt out of your data being used for training. Until then, don't paste in anything you'd consider confidential." },
  { myth: "AI content is instantly recognizable and will embarrass me.", truth: "Generic prompts produce generic writing. Give the AI your voice, your details, and an example of your style, and edit the result — nobody is grading you on whether a tool helped you draft it." },
];

$("#basicsArea").innerHTML = `
  <div class="basics-grid">
    ${explainers.map(x => `
      <div class="explainer">
        <h3><span>${x.icon}</span>${esc(x.title)}</h3>
        ${x.body.map(p => `<p>${esc(p)}</p>`).join("")}
        <div class="analogy">💡 ${esc(x.analogy)}</div>
      </div>`).join("")}
  </div>
  <div class="section-block">
    <h2>Myths vs. reality</h2>
    ${myths.map(m => `
      <div class="myth-row">
        <div class="myth-cell myth"><strong>Myth</strong>${esc(m.myth)}</div>
        <div class="myth-cell truth"><strong>Reality</strong>${esc(m.truth)}</div>
      </div>`).join("")}
  </div>`;

/* ---------- glossary ---------- */
const glossary = [
  ["AI (Artificial Intelligence)", "Software that performs tasks that normally need human intelligence — writing, answering questions, recognizing images, making predictions."],
  ["AI assistant / chatbot", "A tool you talk to in plain English (ChatGPT, Claude, Gemini, Copilot). You type a request; it responds. The main way most businesses use AI."],
  ["Prompt", "What you type to an AI. Better briefs get better results — include who you are, the audience, the tone, and what 'good' looks like."],
  ["LLM (Large Language Model)", "The technology behind AI assistants — a model trained on huge amounts of text so it can understand and generate language. You never need to touch one directly."],
  ["Hallucination", "When an AI confidently states something false. The reason you always fact-check names, numbers, prices, and legal claims before publishing."],
  ["Generative AI", "AI that creates new content — text, images, audio, video — rather than just analyzing existing data."],
  ["Automation", "Making a task run without you. 'AI automation' means the AI does the step a human used to do, e.g. drafting a reply to every new review."],
  ["Chatbot (website)", "A chat window on your site that answers visitor questions automatically, usually trained on your FAQ and business info."],
  ["Training data", "The text and images an AI learned from. Also why you check privacy settings — some free tools may use your chats to improve their models unless you opt out."],
  ["Token", "How AI tools measure text (roughly ¾ of a word). Only matters because some pricing and limits are counted in tokens."],
  ["Context / context window", "How much the AI can 'keep in mind' at once — your instructions plus any documents you paste in. Modern tools handle whole reports easily."],
  ["Fine-tuning", "Customizing an AI model on your own data. Expensive and almost never needed for a small business — good prompts get you 95% of the way."],
  ["RAG (Retrieval-Augmented Generation)", "A technique where the AI looks things up in your documents before answering. It's what makes 'chat with your own files' features work."],
  ["Agent / agentic AI", "AI that can take multi-step actions (browse, fill forms, use tools), not just chat. Powerful, newer — treat it like a junior employee and review its work."],
  ["Copilot", "A general label for AI built into software you already use (Microsoft 365 Copilot, GitHub Copilot). Also Microsoft's brand for its assistant."],
  ["GPT", "The family of models behind ChatGPT (from OpenAI). You'll see the name everywhere; you don't need to know the versions."],
  ["Machine learning", "The broader field: software that learns patterns from data instead of following hand-written rules. Modern AI is built on it."],
  ["Model", "One specific AI 'brain' (e.g. a version of GPT, Claude, or Gemini). Newer models are generally smarter; the tool picks a sensible default for you."],
  ["Open source model", "A model anyone can download and run themselves. Relevant to you only if you have unusual privacy needs and IT help."],
  ["API", "The plumbing that lets one program talk to another. If a freelancer 'connects your booking system to AI via the API', this is what they mean."],
  ["Text-to-image / image generation", "AI that creates pictures from a description. Useful for social graphics and mock-ups; check licensing before commercial use."],
  ["Transcription", "Turning speech into text. AI transcription powers meeting-notes tools and lets you dictate rough thoughts that AI then cleans up."],
  ["Workflow / integration tool", "Services like Zapier or Make that connect your apps so things happen automatically ('when a form is submitted, draft a reply')."],
  ["Free tier", "The no-cost version of a tool, usually with daily limits or older models. The right place to start; upgrade only when you hit the limits."],
  ["Data privacy / opt-out", "Settings that stop a tool from using your conversations to train its models. Check for this before pasting in anything sensitive."],
];

function renderGlossary(filter = "") {
  const q = filter.trim().toLowerCase();
  const items = glossary.filter(([t, d]) => !q || t.toLowerCase().includes(q) || d.toLowerCase().includes(q));
  $("#glossaryList").innerHTML = items.length
    ? items.map(([t, d]) => `<dl class="gloss-item"><dt>${esc(t)}</dt><dd>${esc(d)}</dd></dl>`).join("")
    : `<p class="gloss-empty">No matches — try a shorter word, like "prompt" or "model".</p>`;
}
renderGlossary();
$("#glossarySearch").addEventListener("input", (e) => renderGlossary(e.target.value));

/* ---------- FAQ ---------- */
const faqs = [
  ["How much does it cost to get started?", "Nothing, honestly. ChatGPT, Claude, Gemini, and Copilot all have free tiers that are plenty for learning. When you hit the free limits and it's earning its keep, the paid plans are $20–30/month. You should not be spending hundreds a month unless a specific tool is clearly making or saving you more than it costs."],
  ["Which AI assistant should I pick?", "For your first month it barely matters — they're all excellent and work the same way (a chat box). Pick one, use it daily for two weeks, and you'll know if it fits. If your business lives in Microsoft 365, try Copilot; if in Google Workspace, try Gemini; otherwise ChatGPT or Claude are great defaults."],
  ["Is my data safe? Can I paste customer information in?", "Rule of thumb: don't paste in anything you wouldn't put in an email to an outside contractor — full customer lists, card numbers, medical details, payroll. For everyday drafting you rarely need real personal data anyway ('a customer' works as well as their name). If you want AI on sensitive workflows, use a business plan with a no-training-on-your-data commitment."],
  ["Will Google or my customers punish me for AI-written content?", "Search engines say they rank content by usefulness, not by who typed it. The real risk is publishing generic, unedited AI text — it reads hollow and customers can tell. Use AI for the draft, then add your specifics, your voice, and your photos. Edited AI content is indistinguishable from 'you, faster'."],
  ["Do I need to tell customers I use AI?", "For internal drafting (emails, posts, descriptions) — no, any more than you'd disclose using spellcheck. For a customer-facing chatbot, yes: label it clearly and give an easy path to a human. Some industries (legal, medical, finance) have disclosure rules — check yours."],
  ["What about copyright — can I use what AI writes and draws?", "Text you generate is generally yours to use commercially under the major tools' terms. Images are murkier: rules vary by tool and country, and purely AI-made images may not be copyrightable at all. Fine for social posts and internal use; for a logo or anything trademark-adjacent, involve a human designer."],
  ["My industry is regulated (health, legal, finance). Can I still use AI?", "Yes — for marketing, internal drafts, summaries, and admin. Be much more careful with anything involving client data or advice: check your regulator's guidance, use business-grade tools with data agreements, and keep a human review on everything that goes to a client."],
  ["How do I get my staff on board?", "Frame it as 'this takes the boring parts off your plate', not 'this is watching you'. Pick one or two curious employees to experiment first, have them demo wins at a team meeting, and write a one-page policy (see Staying Safe) so people know what's OK to use it for. Fear usually evaporates after the first hour of hands-on use."],
  ["What if I try it and the answers are bad?", "Nine times out of ten the fix is a better brief: add who you are, who it's for, the tone, length, and an example you like. Also try saying what's wrong — 'shorter, warmer, no exclamation marks' — instead of starting over. The AI improves the draft; you never have to accept version one."],
  ["Is this all a fad? Should I wait for it to settle down?", "The tools will keep changing; the skill of delegating work to AI clearly won't. Owners who build that habit now are compounding a speed advantage every week. You don't need to bet big — $0 and two hours is enough to find out what it does for you."],
];
$("#faqList").innerHTML = faqs.map(([q, a]) => `
  <div class="accordion">
    <button class="accordion-head">${esc(q)}<span class="chev">▶</span></button>
    <div class="accordion-body"><p>${esc(a)}</p></div>
  </div>`).join("");

document.addEventListener("click", (e) => {
  const head = e.target.closest(".accordion-head");
  if (head) head.parentElement.classList.toggle("open");
});

/* ============================================================
   USE CASES
============================================================ */
const industries = [
  {
    emoji: "🍽️", name: "Restaurant / Café", sub: "Front of house, marketing, menus",
    items: [
      ["Social media on autopilot", "give AI this week's specials and it drafts a week of posts with captions in your voice."],
      ["Review replies", "paste in each Google/Yelp review and get a warm, personal reply to approve in seconds."],
      ["Menu engineering", "have AI rewrite bland item descriptions into appetizing ones, and translate the menu for tourists."],
      ["Reservation & FAQ chatbot", "answers 'are you open Monday?', 'do you do gluten-free?' on your site and socials while you're on the pass."],
      ["Staff scheduling drafts", "describe availability and rules; AI drafts the roster you then adjust."],
    ],
    impact: "Typical win: 4–6 hrs/week on marketing & replies"
  },
  {
    emoji: "🔧", name: "Trades & Home Services", sub: "Plumbers, electricians, builders, HVAC",
    items: [
      ["Quotes in minutes", "dictate job details from the van; AI turns them into a professional written quote or estimate."],
      ["Missed-call & after-hours texts", "AI answering services capture job details from callers when you're on the tools."],
      ["Job write-ups & invoices", "rough notes become clear job summaries customers understand (and pay faster)."],
      ["Local SEO content", "monthly blog posts like 'Why is my boiler making that noise?' that bring locals to your site."],
      ["Follow-up sequences", "polite payment reminders and 'time for your annual service?' messages, drafted automatically."],
    ],
    impact: "Typical win: quotes out same-day instead of Sunday night"
  },
  {
    emoji: "🛍️", name: "Retail Shop / E-commerce", sub: "Physical stores and online sellers",
    items: [
      ["Product descriptions at scale", "turn a spec list into persuasive, consistent descriptions for every item in the store."],
      ["Customer service drafts", "AI drafts responses to 'where's my order?' and return requests; you approve and send."],
      ["Email campaigns", "seasonal promos, win-back emails, and new-arrival announcements drafted in your brand voice."],
      ["Review mining", "paste 100 reviews and ask what customers love, hate, and wish you stocked."],
      ["Ad copy variants", "10 versions of a Facebook/Instagram ad to test, in 30 seconds."],
    ],
    impact: "Typical win: product listings done 5–10× faster"
  },
  {
    emoji: "💇", name: "Salon / Spa / Fitness", sub: "Appointment-based personal services",
    items: [
      ["No-show reduction", "friendly reminder and rebooking messages drafted and scheduled automatically."],
      ["Instagram content engine", "AI plans a month of posts, reels ideas, and captions from a single brainstorm."],
      ["Client FAQ chatbot", "prices, cancellation policy, and 'what should I do before my appointment?' answered 24/7."],
      ["Retail recommendations", "turn product knowledge into simple 'if hair type X, suggest Y' scripts for the front desk."],
      ["Membership win-back", "personalized 'we miss you' campaigns for lapsed clients."],
    ],
    impact: "Typical win: consistent marketing without a marketing hire"
  },
  {
    emoji: "📊", name: "Professional Services", sub: "Accountants, lawyers, consultants, agencies",
    items: [
      ["Document summarization", "condense long contracts, filings, or reports into briefing notes before a client call."],
      ["First-draft engine", "engagement letters, proposals, and client memos drafted from your bullet points."],
      ["Meeting notes & actions", "AI notetakers turn every client call into a summary with action items already assigned."],
      ["Plain-English client comms", "translate technical or legal language into emails clients actually understand."],
      ["Research assistant", "get oriented on an unfamiliar topic fast — then verify against primary sources."],
    ],
    impact: "Typical win: 1–2 billable hours reclaimed per day"
  },
  {
    emoji: "🏠", name: "Real Estate", sub: "Agents and property managers",
    items: [
      ["Listing descriptions", "property facts in, compelling listing copy out — in minutes, for every listing."],
      ["Lead follow-up", "instant, personalized responses to portal inquiries so leads don't go cold."],
      ["Market update newsletters", "monthly area updates drafted from data you paste in."],
      ["Tenant communications", "maintenance updates, notice letters, and renewals drafted consistently and politely."],
      ["Social tours", "turn a listing into a script for a walkthrough video plus post captions."],
    ],
    impact: "Typical win: every listing marketed like a flagship"
  },
  {
    emoji: "🦷", name: "Clinics & Practices", sub: "Dental, medical, physio, vet (admin side)",
    items: [
      ["Recall & reminder messages", "friendly, clear appointment reminders and recall campaigns that cut no-shows."],
      ["Patient FAQ content", "website pages answering common questions in reassuring plain English."],
      ["Admin letters", "referral thank-yous, welcome packs, and policy letters drafted for staff to review."],
      ["Review responses", "compliant, warm replies to public reviews (never confirming someone is a patient)."],
      ["Training materials", "turn your procedures into checklists and onboarding docs for new front-desk staff."],
    ],
    impact: "Note: keep patient data out of general AI tools — see Staying Safe"
  },
  {
    emoji: "🚗", name: "Auto Repair / Dealership", sub: "Garages, mechanics, detailers",
    items: [
      ["Estimate explanations", "turn a parts-and-labor list into a plain-English explanation customers trust."],
      ["Service reminders", "MOT/inspection and service-due campaigns drafted and personalized."],
      ["'What's that noise?' content", "SEO posts and social videos scripted around the questions drivers actually Google."],
      ["Upsell scripts", "honest, non-pushy ways to explain why the air filter matters."],
      ["Warranty letters", "clear written summaries of work done, for the glovebox and your records."],
    ],
    impact: "Typical win: customers who understand the bill argue less"
  },
];

const functions_ = [
  {
    emoji: "📣", name: "Marketing", sub: "The #1 place to start for most businesses",
    items: [
      ["Social posts & captions", "a month of content from one 20-minute brainstorm."],
      ["Email newsletters & promos", "drafted in your voice, ready in minutes."],
      ["Ad copy & headlines", "generate 10 variants, test the best two."],
      ["SEO blog posts", "answer the questions your customers Google."],
      ["Brand voice guide", "have AI interview you, then write your voice rules so all content sounds like you."],
    ],
    impact: "Lowest risk, fastest visible payoff"
  },
  {
    emoji: "🎧", name: "Customer Service", sub: "Faster answers, calmer inbox",
    items: [
      ["Reply drafting", "paste any customer email and get a considered reply to edit and send."],
      ["FAQ chatbot", "your website answers the same 20 questions automatically, 24/7."],
      ["Review management", "thoughtful responses to every review, good and bad."],
      ["Tone rescue", "turn your frustrated draft into the professional version before you hit send."],
      ["Help-page writing", "turn your answers into a proper FAQ page so questions stop arriving."],
    ],
    impact: "Response times drop from days to minutes"
  },
  {
    emoji: "🗂️", name: "Admin & Operations", sub: "The invisible time-eaters",
    items: [
      ["Meeting notes", "AI notetakers produce summaries and action lists from every call."],
      ["Document drafting", "policies, SOPs, checklists, and letters from bullet points."],
      ["Inbox triage", "summarize long threads; draft the routine replies."],
      ["Spreadsheet help", "describe what you want ('flag customers who haven't ordered in 90 days') and get the formula or the analysis."],
      ["Translation", "communicate cleanly with suppliers and customers in other languages."],
    ],
    impact: "Often 3–5 hrs/week back, immediately"
  },
  {
    emoji: "🤝", name: "Sales", sub: "More follow-up, less friction",
    items: [
      ["Proposal & quote drafts", "professional documents from rough job notes."],
      ["Follow-up sequences", "polite persistence, written once, personalized each time."],
      ["Lead research", "summarize a prospect's website and news before the call."],
      ["Objection practice", "role-play the price conversation with AI before you have it for real."],
      ["CRM hygiene", "turn call notes into clean CRM entries and next steps."],
    ],
    impact: "The fortune is in the follow-up — now it actually happens"
  },
  {
    emoji: "👥", name: "Hiring & HR", sub: "Small-team people ops",
    items: [
      ["Job descriptions", "clear, appealing ads drafted from your requirements."],
      ["Interview kits", "role-specific questions and scoring guides."],
      ["Onboarding docs", "first-week checklists and how-we-work guides."],
      ["Policy drafts", "leave, expenses, and conduct policies to review with a professional."],
      ["Sensitive-message drafts", "difficult conversations planned and worded carefully. Always keep human judgment on hiring decisions — see Staying Safe."],
    ],
    impact: "A one-person HR department's output, part-time"
  },
  {
    emoji: "🧮", name: "Finance & Planning", sub: "Clarity without a CFO",
    items: [
      ["Plain-English explanations", "paste your P&L and ask what changed and why it matters."],
      ["Scenario thinking", "'What happens to margin if supplier costs rise 8%?' — structured thinking in seconds."],
      ["Payment chasing", "firm-but-friendly overdue invoice sequences."],
      ["Budget drafts", "a starting structure for next year's budget to refine with your accountant."],
      ["Grant & loan applications", "first drafts of the narrative sections. Verify every number yourself — AI arithmetic on your real books needs checking."],
    ],
    impact: "Better questions for your accountant, cheaper meetings"
  },
];

function renderUsecases(view) {
  const data = view === "industry" ? industries : functions_;
  $("#usecaseArea").innerHTML = `<div class="industry-grid">
    ${data.map(d => `
      <div class="industry-card">
        <div class="ic-head"><span class="ic-emoji">${d.emoji}</span><div><h3>${esc(d.name)}</h3><div class="ic-sub">${esc(d.sub)}</div></div></div>
        <ul>${d.items.map(([t, b]) => `<li><strong>${esc(t)}:</strong> ${esc(b)}</li>`).join("")}</ul>
        <span class="impact-tag">${esc(d.impact)}</span>
      </div>`).join("")}
  </div>`;
}
renderUsecases("industry");
$$("[data-ucview]").forEach(btn => btn.addEventListener("click", () => {
  $$("[data-ucview]").forEach(b => b.classList.remove("active"));
  btn.classList.add("active");
  renderUsecases(btn.dataset.ucview);
}));

/* ============================================================
   WALKTHROUGHS
============================================================ */
const walkthroughs = [
  {
    title: "Your very first AI session (start here)",
    time: "15 min", cost: "Free", level: "Complete beginner",
    goal: "Have your first useful conversation with an AI assistant and leave with something you can actually use.",
    steps: [
      ["Open an AI assistant", "In your web browser, go to chatgpt.com, claude.ai, or gemini.google.com. Create a free account with your email (exactly like signing up for anything else)."],
      ["Find the chat box", "You'll see a single text box, like a messaging app. That's the whole interface. Click in it."],
      ["Introduce your business", "Type something like: \"I run a 12-table Italian restaurant in Leeds called Rosa's. Family-run, 15 years, known for fresh pasta. I'm going to ask you to help with some business tasks.\" Press Enter."],
      ["Ask for something real", "Try: \"Write a warm 3-sentence reply to a customer review that said the food was great but service was slow on a busy Saturday.\" Read what comes back."],
      ["Push back — this is the magic step", "Don't accept draft one. Type: \"Warmer. Mention we've added a server on weekends. Don't use the word 'apologize' twice.\" Watch it revise instantly. This back-and-forth is the entire skill."],
      ["Save the good bits", "Copy anything useful into your notes. Then ask: \"What are 10 tasks in a business like mine you could help with every week?\" — that's your project list for the month."],
    ],
    tip: "The chat remembers your earlier messages in the same conversation, so you only explain your business once per chat. Tomorrow, start a new chat and paste the same intro — or use the tool's 'memory' or 'custom instructions' settings so it always knows who you are."
  },
  {
    title: "Write a marketing email that sounds like you",
    time: "20 min", cost: "Free", level: "Beginner",
    goal: "A ready-to-send promotional email in your own voice — and a reusable 'voice brief' for all future marketing.",
    steps: [
      ["Gather your ingredients", "Find one past email or post you wrote that sounds like you, plus the details of what you're promoting (offer, dates, who it's for)."],
      ["Teach the AI your voice", "Paste your past email and say: \"This is my writing style. Describe it in a short list of rules I can reuse.\" Save that list — it's your voice brief."],
      ["Brief the task", "Type: \"Using that style, write a promotional email to past customers. Offer: [YOUR OFFER]. Goal: bookings for [DATES]. Length: under 150 words. One clear call to action.\""],
      ["Revise like a boss", "Give directions, not essays: \"Subject line options?\", \"Less salesy\", \"Add a PS about gift cards\". Two or three rounds gets it right."],
      ["Safety pass", "Check every fact yourself: prices, dates, terms. AI happily invents plausible details — this pass is non-negotiable."],
      ["Send it from your normal email tool", "Copy into Mailchimp, Gmail, whatever you use today. Note how long the whole thing took versus your usual staring-at-a-blank-page hour."],
    ],
    tip: "Keep the 'voice brief' in a note. Starting every marketing chat by pasting it in means everything comes out sounding like you, not like a robot."
  },
  {
    title: "Turn one idea into a month of social media",
    time: "45 min", cost: "Free", level: "Beginner",
    goal: "A 4-week content calendar with ready-to-post captions, from a single brainstorming session.",
    steps: [
      ["Brain-dump to the AI", "Tell it: your business, your customers, what you sell most, anything coming up (seasons, events, offers). Rough and unpolished is fine — that's the point."],
      ["Ask for the plan", "\"Create a 4-week social media calendar for my business: 3 posts per week, mixing tips, behind-the-scenes, and promotion. Give each post a topic and a caption in my voice, with hashtag suggestions.\""],
      ["Cull and improve", "Delete the weak third, then: \"Replace posts 4 and 9 — more about [TOPIC]\" and \"Make the captions shorter and punchier.\""],
      ["Add the human layer", "AI writes captions; you supply real photos. Phone photos of actual work, products, and people outperform stock images every time."],
      ["Schedule it", "Load the month into a free scheduler (Meta Business Suite is free for Facebook/Instagram, or Buffer's free plan) in one sitting."],
      ["Repeat monthly, faster", "Next month, paste this month's calendar and say: \"Same format. New month. Here's what's coming up: …\" — this drops to ~20 minutes."],
    ],
    tip: "Ask the AI for '5 post ideas only a real [YOUR TRADE] would know' — insider knowledge posts are the ones that get shared."
  },
  {
    title: "Set up an FAQ chatbot on your website",
    time: "1–2 hrs", cost: "$0–50/mo", level: "Intermediate",
    goal: "A chat widget on your site that correctly answers your 20 most common questions, 24/7, and hands anything else to you.",
    steps: [
      ["Collect the real questions", "Write down the 15–25 questions you actually get: hours, prices, parking, cancellations, do-you-do-X. Check your email and messages for the exact wording customers use."],
      ["Draft the answer sheet with AI", "Paste the list into your assistant: \"Write clear, friendly 2–3 sentence answers to each, based on this info about my business: [DETAILS].\" Correct anything wrong — this document is your chatbot's brain."],
      ["Pick a chatbot service", "Look for: 'trained on your content', a free tier or trial, and a widget for your website platform (Squarespace, Wix, WordPress, Shopify all have options in their app stores). Typical paid tiers run $20–50/month."],
      ["Feed it your content", "Upload your answer sheet and point it at your website pages. This takes minutes in modern tools — no coding."],
      ["Set the guardrails", "Configure: a clear name like 'Rosa's Assistant (automated)', instructions to only answer from your provided info, and a fallback — \"I'm not sure — leave your email and we'll reply today.\" Never let it guess prices or make promises."],
      ["Test like a difficult customer", "Ask it 20 questions including weird ones. Try to trick it. Fix wrong answers by editing your answer sheet. Only then switch it live."],
      ["Review weekly at first", "Read the chat logs for the first few weeks. You'll find new questions to add and phrasings to fix — the bot gets noticeably better each pass."],
    ],
    tip: "A chatbot that says 'I don't know, here's how to reach a human' builds trust. One that makes up an answer costs you a customer. Guardrails first, cleverness second."
  },
  {
    title: "Never take meeting notes again",
    time: "30 min setup", cost: "$0–20/mo", level: "Beginner",
    goal: "Every call and meeting produces an automatic summary with action items, without you writing a word.",
    steps: [
      ["Pick a notetaker", "Search your video-call tool's app store (Zoom, Teams, Meet) for an 'AI notetaker' or use a standalone one. Free tiers typically cover a few meetings a month; paid is ~$10–20."],
      ["Connect your calendar", "The notetaker joins scheduled calls automatically, or you tap 'record' for in-person meetings on your phone."],
      ["Tell people — always", "Recording conversations without consent is illegal in many places and bad manners everywhere. Standard line: \"I use an AI notetaker so I can pay attention instead of scribbling — OK with everyone?\""],
      ["Check the first few summaries", "Read them against your memory of the meeting. Fix its vocabulary (it may mangle product names) — most tools let you add custom terms."],
      ["Build the habit that pays", "After each meeting, forward the action list to whoever owes what. Follow-through, not the notes themselves, is where this saves relationships and money."],
    ],
    tip: "For client-confidential work, check the notetaker's privacy terms and your industry's rules before recording anything sensitive — see Staying Safe."
  },
  {
    title: "Mine your reviews & feedback for gold",
    time: "30 min", cost: "Free", level: "Beginner",
    goal: "Know exactly what customers love, what's costing you repeat business, and what to fix first — from data you already have.",
    steps: [
      ["Gather the raw material", "Copy your last 50–100 reviews (Google, Yelp, Facebook, surveys) into one document. Messy formatting is fine."],
      ["Ask for the analysis", "Paste it all in: \"Analyze these customer reviews. Give me: top 5 things customers love, top 5 complaints, any trends over time, and 3 specific improvements ranked by likely impact.\""],
      ["Interrogate the results", "Follow up: \"Show me quotes supporting complaint #2\", \"Which complaints mention staff by name?\", \"Draft how I'd brief my team on fixing #1.\""],
      ["Turn insight into marketing", "\"Based on what customers love most, write 3 social posts that lean into those strengths\" — marketing built on evidence, not guesswork."],
      ["Repeat quarterly", "Same exercise every quarter and ask: \"Compare with the summary from last quarter — what improved, what got worse?\""],
    ],
    tip: "This works on any text pile: survey responses, complaint emails, staff exit interviews. If you have 20+ pieces of feedback anywhere, there's a pattern in it you haven't seen."
  },
  {
    title: "Quotes & proposals from voice notes",
    time: "30 min setup", cost: "Free", level: "Beginner",
    goal: "Turn 2 minutes of talking after a site visit or sales call into a professional quote the same day.",
    steps: [
      ["Record a voice note", "Right after the job visit, ramble into your phone: what the job is, materials, rough hours, anything unusual, the price you have in mind."],
      ["Get it transcribed", "Use your phone's built-in transcription, or the AI assistant apps' voice input — talk directly into the chat."],
      ["Set up your template once", "Give the AI a past quote you're proud of: \"This is my quote format and tone. I'll send you rough job notes; turn them into quotes in exactly this format.\""],
      ["Generate and check", "Paste the transcript. Check every number, measurement, and price on the draft — you're the estimator, AI is just the typist."],
      ["Send same-day", "The trade that quotes first, wins — same-day beats 'sometime next week' more often than a lower price does."],
      ["Save each one", "Keep the chat (or a document of past quotes). \"Like the Henderson job but with two bathrooms\" gets faster every time."],
    ],
    tip: "Add a line to your template asking the AI to flag anything missing: \"If the notes don't mention access, timeline, or disposal, add a QUESTIONS section instead of guessing.\""
  },
  {
    title: "Automate a task end-to-end (your first automation)",
    time: "1–2 hrs", cost: "$0–30/mo", level: "Intermediate",
    goal: "One real task that happens completely without you — the gateway to serious time savings.",
    steps: [
      ["Choose a bridge task", "Good first automations: new review → drafted reply in your inbox; contact-form message → summarized and categorized; new booking → personalized confirmation. Pick something annoying, frequent, and low-stakes."],
      ["Check for built-in AI first", "Open the settings of the tool you already use (booking system, e-commerce platform, review manager). Many added AI reply/draft features in the last two years — free and zero setup."],
      ["If not: use a connector tool", "Zapier and Make connect your apps to AI without code. The recipe is always: Trigger (new review arrives) → AI step (draft a reply using your instructions) → Action (email the draft to me). Both have templates for exactly these — search their template galleries."],
      ["Write the AI's standing orders", "In the AI step, paste permanent instructions: your business info, tone rules, what it must never say (prices, promises, refunds). Treat it like the world's most literal employee."],
      ["Run it with a human checkpoint", "First month: automation drafts, you approve and send. Only remove the checkpoint for genuinely low-stakes messages, if ever."],
      ["Review after 30 days", "Count: how many drafts, how many needed edits, minutes saved. Keep, fix, or kill it based on the numbers, then pick automation #2."],
    ],
    tip: "The approval-checkpoint pattern (AI drafts → you tap approve) captures ~90% of the time savings with ~10% of the risk. Full autopilot is rarely worth it for customer-facing messages."
  },
];

$("#walkthroughArea").innerHTML = walkthroughs.map((w, i) => `
  <div class="accordion ${i === 0 ? "open" : ""}">
    <button class="accordion-head">${esc(w.title)}<span class="chev">▶</span></button>
    <div class="accordion-body">
      <div class="wt-meta">
        <span class="wt-tag time">⏱ ${esc(w.time)}</span>
        <span class="wt-tag cost">💵 ${esc(w.cost)}</span>
        <span class="wt-tag level">📶 ${esc(w.level)}</span>
      </div>
      <div class="wt-goal"><strong>What you'll have at the end:</strong> ${esc(w.goal)}</div>
      <ol class="steps">
        ${w.steps.map(([t, b]) => `<li><strong>${esc(t)}</strong>${esc(b)}</li>`).join("")}
      </ol>
      <div class="wt-tip"><strong>Pro tip:</strong> ${esc(w.tip)}</div>
    </div>
  </div>`).join("");

/* ============================================================
   PROMPTS
============================================================ */
const promptCats = ["All", "Marketing", "Customer Service", "Admin & Ops", "Sales", "Hiring", "Finance", "Strategy"];
const prompts = [
  { cat: "Marketing", title: "Month of social posts", text: "You are a social media manager for my business. Business: [WHAT YOU DO, WHERE, WHO FOR]. Voice: [e.g. friendly, expert, a little funny]. Create a 4-week calendar: 3 posts per week mixing helpful tips, behind-the-scenes, and one promotion per week. For each post give me the topic, a caption under 60 words, and 3 hashtags." },
  { cat: "Marketing", title: "Promo email", text: "Write a promotional email to past customers of my [BUSINESS TYPE]. Offer: [OFFER + DATES]. Voice: warm, no hype, no exclamation marks. Under 150 words, one clear call to action, plus 3 subject line options under 45 characters." },
  { cat: "Marketing", title: "Google Business profile post", text: "Write a Google Business Profile update for [BUSINESS] announcing [NEWS/OFFER]. Under 100 words, plain language, include a call to action. Also suggest the best category of photo to attach." },
  { cat: "Marketing", title: "SEO blog post", text: "Write a 600-word blog post for my [BUSINESS TYPE] website answering: \"[QUESTION CUSTOMERS GOOGLE]\". Plain English, short paragraphs, useful even to someone who won't hire us, ending with a soft invitation to get in touch. Suggest a title under 60 characters and a meta description under 155 characters." },
  { cat: "Marketing", title: "Find my brand voice", text: "Here are 3 things I've written that sound like me: [PASTE]. Describe my writing voice as 6 short rules a writer could follow (e.g. 'short sentences', 'no jargon', 'dry humor'). I'll use these rules in future prompts." },
  { cat: "Customer Service", title: "Reply to a negative review", text: "A customer left this review of my [BUSINESS]: \"[PASTE REVIEW]\". Write a public reply that: thanks them, takes responsibility without groveling, states one concrete thing we're doing about it, and invites them back. Under 80 words. No corporate clichés like 'we take this seriously'." },
  { cat: "Customer Service", title: "Reply to any customer email", text: "Draft a reply to this customer email: \"[PASTE EMAIL]\". Context: [WHAT ACTUALLY HAPPENED / WHAT YOU CAN OFFER]. Tone: professional, warm, solution-first. If the customer is angry, acknowledge it in the first sentence. Keep it under 120 words." },
  { cat: "Customer Service", title: "De-escalate my angry draft", text: "I wrote this reply while annoyed: \"[PASTE YOUR DRAFT]\". Rewrite it so it is firm on the facts but completely professional in tone. Keep my key points. Show me a short version and a fuller version." },
  { cat: "Customer Service", title: "Build an FAQ page", text: "Here are questions customers keep asking, with my rough answers: [PASTE LIST]. Turn this into a clean FAQ page: group related questions, rewrite answers in 2–3 friendly sentences each, and flag any question where my answer seems incomplete." },
  { cat: "Admin & Ops", title: "Summarize a long document", text: "Summarize the following document for a busy business owner. Give me: 5-bullet summary, any deadlines or amounts I must not miss, anything unusual or risky, and questions I should ask before signing/agreeing. Document: [PASTE]." },
  { cat: "Admin & Ops", title: "Write an SOP from my ramble", text: "I'm going to describe how we do a task, messily. Turn it into a numbered standard-operating-procedure a brand-new employee could follow, with a 'common mistakes' section at the end. Here's the ramble: [PASTE/DICTATE]." },
  { cat: "Admin & Ops", title: "Untangle an email thread", text: "Here's a long email thread: [PASTE]. Tell me: what's actually being decided, who is waiting on whom, and draft the single email from me that moves this forward." },
  { cat: "Admin & Ops", title: "Spreadsheet formula help", text: "I use [Excel/Google Sheets]. My sheet has columns: [e.g. A: customer name, B: last order date, C: total spent]. I want: [e.g. to highlight customers with no order in 90 days]. Give me the exact formula or steps, and explain it in one sentence." },
  { cat: "Sales", title: "Quote from job notes", text: "Turn these rough job notes into a professional quote: [PASTE NOTES]. Format: brief intro, scope of work as bullets, price section: [YOUR PRICING], what's included/excluded, validity period of [X DAYS]. Tone: confident and clear. If any key detail is missing from my notes, list it as a question instead of inventing it." },
  { cat: "Sales", title: "Follow-up sequence", text: "Write a 3-message follow-up sequence for a prospect who received my quote for [SERVICE] but hasn't replied. Message 1 (day 3): helpful check-in. Message 2 (day 8): add one piece of value or reassurance. Message 3 (day 15): polite close-the-loop. Each under 80 words, none of them pushy." },
  { cat: "Sales", title: "Pre-call research brief", text: "I'm meeting a potential client. Here's their website 'About' text and anything I know: [PASTE]. Give me: a 5-line summary of their business, 3 problems a [YOUR SERVICE] could plausibly solve for them, and 5 good discovery questions to ask." },
  { cat: "Sales", title: "Practice the price objection", text: "Role-play with me. You are a customer who likes my [SERVICE] proposal but says it's too expensive. I'll respond; push back realistically (but stay civil) until I handle it well, then break character and give me feedback on what worked." },
  { cat: "Hiring", title: "Job ad that attracts", text: "Write a job ad for a [ROLE] at my [BUSINESS TYPE] in [LOCATION]. Pay: [RANGE]. What actually makes working here good: [HONEST LIST]. Plain language, no corporate buzzwords, honest about the hard parts of the job, and a simple 'how to apply' that filters for people who read the ad." },
  { cat: "Hiring", title: "Interview kit", text: "Create an interview kit for hiring a [ROLE]: 8 questions mixing skills, situational judgment, and values; what a strong vs. weak answer looks like for each; and 3 legally risky topics to avoid in interviews in [COUNTRY/STATE]." },
  { cat: "Hiring", title: "First-week onboarding plan", text: "Create a first-week onboarding plan for a new [ROLE] at my [BUSINESS TYPE]. Day-by-day: what they learn, who they shadow, what they should be able to do alone by Friday. Include a checklist I can print." },
  { cat: "Finance", title: "Explain my numbers", text: "I'm pasting simplified numbers from my business: [PASTE P&L / REVENUE BY MONTH etc.]. Explain in plain English: the 3 most important trends, anything that looks unhealthy, and the 3 best questions to ask my accountant. Do not just restate the numbers — interpret them. Note: double-check any arithmetic you do." },
  { cat: "Finance", title: "Chase an overdue invoice", text: "Write a sequence of 3 payment-reminder emails for an invoice [X DAYS] overdue from a customer I want to keep. Escalate gently: friendly nudge → firm reminder with due date → final notice mentioning next steps. Keep all three professional and short." },
  { cat: "Strategy", title: "Annual plan sparring partner", text: "Act as a tough but constructive business advisor. My business: [DESCRIPTION, ROUGH NUMBERS, GOALS]. Interview me one question at a time (max 8 questions) about my plans for next year, then give me: 3 biggest risks you see, 3 opportunities I may be missing, and the one thing you'd focus on first." },
  { cat: "Strategy", title: "Competitor teardown", text: "Here is the website text / marketing of a competitor: [PASTE]. Analyze: who they're targeting, their pricing signals, their strengths, their weaknesses, and 3 specific ways my business ([YOUR EDGE]) could position against them. Base it only on what I pasted — flag guesses as guesses." },
  { cat: "Strategy", title: "Should I do X? — decision helper", text: "Help me think through a decision: [DESCRIBE, e.g. 'opening a second location' / 'raising prices 10%']. Ask me up to 5 clarifying questions first. Then give me: the strongest case for, the strongest case against, what information would change the answer, and a small low-risk way to test it." },
];

$("#promptFilters").innerHTML = promptCats.map((c, i) =>
  `<button class="toggle-btn ${i === 0 ? "active" : ""}" data-pcat="${esc(c)}">${esc(c)}</button>`).join("");

function renderPrompts(cat) {
  const list = prompts.filter(p => cat === "All" || p.cat === cat);
  $("#promptArea").innerHTML = `<div class="prompt-grid">
    ${list.map((p, i) => `
      <div class="prompt-card">
        <span class="cat-tag">${esc(p.cat)}</span>
        <h3>${esc(p.title)}</h3>
        <div class="prompt-text" id="pt-${i}">${esc(p.text)}</div>
        <button class="copy-btn" data-copy="pt-${i}">📋 Copy prompt</button>
      </div>`).join("")}
  </div>`;
}
renderPrompts("All");
$$("[data-pcat]").forEach(btn => btn.addEventListener("click", () => {
  $$("[data-pcat]").forEach(b => b.classList.remove("active"));
  btn.classList.add("active");
  renderPrompts(btn.dataset.pcat);
}));
document.addEventListener("click", async (e) => {
  const btn = e.target.closest("[data-copy]");
  if (!btn) return;
  const text = $("#" + btn.dataset.copy).textContent;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand("copy"); ta.remove();
  }
  btn.textContent = "✅ Copied!";
  btn.classList.add("copied");
  toast("Prompt copied — paste it into any AI assistant");
  setTimeout(() => { btn.textContent = "📋 Copy prompt"; btn.classList.remove("copied"); }, 1800);
});

/* ============================================================
   TOOLS
============================================================ */
const toolCats = [
  {
    name: "1. All-purpose AI assistants — start here",
    note: "One of these covers 80% of everything in this guide: drafting, summarizing, brainstorming, analysis. Pick one, learn it well. All prices are typical as of 2026 — check current pricing.",
    rows: [
      ["ChatGPT (OpenAI)", "The most widely used. Strong all-rounder; voice mode; can analyze uploaded files and images.", "Free tier · ~$20/mo for Plus"],
      ["Claude (Anthropic)", "Excellent writer — strong for long documents, careful tone, and nuanced business writing.", "Free tier · ~$20/mo for Pro"],
      ["Gemini (Google)", "Built into Gmail, Docs, and Sheets if you use Google Workspace — AI where you already work.", "Free tier · ~$20/mo"],
      ["Microsoft Copilot", "Built into Word, Outlook, Excel, and Teams for Microsoft 365 businesses.", "Basic free · ~$30/user/mo in M365"],
    ]
  },
  {
    name: "2. AI already inside tools you own — check before buying anything",
    note: "Before paying for anything new, open the settings of software you already pay for. Most added real AI features recently — you may already own the tool you need.",
    rows: [
      ["Your email platform", "Mailchimp, Constant Contact etc. now draft campaigns and subject lines with AI.", "Usually included"],
      ["Your website builder", "Wix, Squarespace, Shopify, WordPress: AI writes pages, product descriptions, and SEO text.", "Usually included"],
      ["Your accounting software", "QuickBooks, Xero: AI categorization, anomaly flags, cash-flow summaries.", "Usually included"],
      ["Your booking / POS / CRM", "Square, Toast, HubSpot and similar: AI replies, summaries, and marketing text.", "Usually included"],
      ["Canva (design)", "AI image generation, background removal, and design suggestions inside the editor most small businesses already use.", "Free tier · ~$13/mo Pro"],
    ]
  },
  {
    name: "3. Specialist tools — add only when a task demands it",
    note: "Each earns its keep on one specific job. Add them one at a time, only after your assistant habit is established, and only if the free trial proves value.",
    rows: [
      ["AI meeting notetakers", "Join your calls, produce summaries and action items automatically (e.g. Otter, Fathom, Fireflies, or your video tool's built-in).", "Free tiers · ~$10–20/mo"],
      ["Website FAQ chatbots", "Trained on your content, answer visitor questions 24/7 (e.g. Chatbase, Tidio, or your platform's app store).", "~$0–50/mo"],
      ["Automation connectors", "Zapier, Make: link your apps with AI steps — 'new review → drafted reply in my inbox'.", "Free tiers · ~$20–30/mo"],
      ["AI phone / missed-call answering", "Captures job details from callers when you can't answer — big for trades and clinics.", "~$30–100/mo"],
      ["Image generation", "Product mock-ups, social graphics (built into ChatGPT, Canva; or Midjourney ~$10/mo).", "Often included · ~$10/mo"],
    ]
  },
];

$("#toolsArea").innerHTML = toolCats.map(tc => `
  <div class="tool-cat">
    <h3>${esc(tc.name)}</h3>
    <p class="tc-note">${esc(tc.note)}</p>
    <div class="tool-table-wrap">
      <table class="tool-table">
        <thead><tr><th>Tool</th><th>What it's for</th><th>Typical cost</th></tr></thead>
        <tbody>
          ${tc.rows.map(r => `<tr><td>${esc(r[0])}</td><td>${esc(r[1])}</td><td><span class="price-pill">${esc(r[2])}</span></td></tr>`).join("")}
        </tbody>
      </table>
    </div>
  </div>`).join("");

/* ---------- ROI calculator ---------- */
$("#roiCalc").innerHTML = `
  <div class="roi-inputs">
    <div class="roi-field">
      <label>Hours per week on writing &amp; marketing <output id="o1">6</output></label>
      <input type="range" id="r1" min="0" max="20" value="6">
      <div class="hint">Emails, social posts, ads, website text, promotions</div>
    </div>
    <div class="roi-field">
      <label>Hours per week on customer messages &amp; reviews <output id="o2">4</output></label>
      <input type="range" id="r2" min="0" max="20" value="4">
      <div class="hint">Replying to inquiries, reviews, FAQs, follow-ups</div>
    </div>
    <div class="roi-field">
      <label>Hours per week on admin &amp; documents <output id="o3">5</output></label>
      <input type="range" id="r3" min="0" max="20" value="5">
      <div class="hint">Quotes, notes, summaries, SOPs, spreadsheets, scheduling</div>
    </div>
    <div class="roi-field">
      <label>What your time is worth per hour ($) <output id="o4">50</output></label>
      <input type="range" id="r4" min="15" max="250" step="5" value="50">
      <div class="hint">Your effective hourly rate — or what you'd pay someone to do it</div>
    </div>
    <div class="roi-field">
      <label>Monthly AI budget ($) <output id="o5">25</output></label>
      <input type="range" id="r5" min="0" max="200" step="5" value="25">
      <div class="hint">$0–30 covers an assistant; ~$50–100 adds a chatbot or notetaker</div>
    </div>
  </div>
  <div class="roi-result">
    <div>
      <div class="roi-line">Estimated time back in your week</div>
      <div class="big" id="roiHours">—</div>
    </div>
    <div>
      <div class="roi-line">Estimated value per year</div>
      <div class="big" id="roiValue">—</div>
    </div>
    <div class="roi-line" id="roiSummary"></div>
  </div>`;

function calcROI() {
  const h1 = +$("#r1").value, h2 = +$("#r2").value, h3 = +$("#r3").value;
  const rate = +$("#r4").value, budget = +$("#r5").value;
  // Conservative: AI reliably shaves ~35% off these task categories once habits form
  const saved = (h1 + h2 + h3) * 0.35;
  const grossYear = saved * rate * 50;           // 50 working weeks
  const costYear = budget * 12;
  const netYear = Math.max(0, grossYear - costYear);
  $("#o1").textContent = h1; $("#o2").textContent = h2; $("#o3").textContent = h3;
  $("#o4").textContent = rate; $("#o5").textContent = budget;
  $("#roiHours").textContent = saved.toFixed(1) + " hrs";
  $("#roiValue").textContent = "$" + Math.round(netYear).toLocaleString();
  $("#roiSummary").innerHTML = grossYear > 0
    ? `That's <strong>${Math.round(saved * 50)} hours a year</strong> — about <strong>${(saved * 50 / 40).toFixed(0)} working weeks</strong> — for an AI spend of $${costYear.toLocaleString()}/yr. We assume AI takes ~35% off these tasks, a deliberately conservative figure.`
    : "Move the sliders to match your week and see what the hours are worth.";
}
$$("#roiCalc input").forEach(i => i.addEventListener("input", calcROI));
calcROI();

/* ============================================================
   SAFETY
============================================================ */
const safetyRules = [
  {
    danger: true, title: "🔒 Never paste in what you'd never email out",
    body: "Customer lists with personal details, card numbers, medical information, payroll, passwords, unreleased financials. Free AI tools may use conversations to improve their models unless you opt out.",
    fix: "Rule for you and staff: if you wouldn't email it to an outside contractor, don't paste it into an AI tool. For sensitive workflows, use a business plan with a written no-training commitment."
  },
  {
    danger: true, title: "🎭 AI states falsehoods confidently",
    body: "It can invent statistics, laws, prices, and even citations — while sounding completely certain. This is the single most common way businesses embarrass themselves with AI.",
    fix: "Verify every fact, number, name, and legal claim before it leaves the building. AI drafts; a human fact-checks anything customer-facing."
  },
  {
    danger: true, title: "⚖️ High-stakes decisions stay human",
    body: "Hiring and firing, credit decisions, medical or legal advice, contract sign-off. Some of these carry legal requirements (and AI-specific regulations are growing, especially around employment decisions).",
    fix: "Use AI to prepare (drafts, summaries, questions) — never to decide. A named human owns every consequential decision, and can explain it without saying 'the AI said so'."
  },
  {
    title: "🧾 Check the arithmetic",
    body: "Language AI is weirdly capable and weirdly bad at math on your real numbers — it can mangle a margin calculation while writing beautifully about it.",
    fix: "Do the math in your spreadsheet or calculator; let AI explain, structure, and draft around numbers you computed."
  },
  {
    title: "📢 Label customer-facing bots",
    body: "People forgive a labeled bot for being limited; they don't forgive being tricked into thinking they were talking to a person.",
    fix: "Name it clearly ('Automated assistant'), give it a one-tap path to a human, and never let it promise prices, refunds, or outcomes."
  },
  {
    title: "🌀 Don't publish generic slop",
    body: "Unedited AI content is recognizable: hollow, over-hedged, weirdly enthusiastic. Publishing it at volume damages the trust your brand runs on.",
    fix: "The 20% you add — real photos, real specifics, your voice, your opinion — is what makes it yours. Edit everything."
  },
  {
    title: "🔑 Basic account hygiene",
    body: "Your AI accounts will accumulate business context — prompts, documents, strategy. Treat them like your email account.",
    fix: "Unique passwords, two-factor authentication on, and remove ex-employees' access the day they leave."
  },
  {
    title: "🎣 AI-powered scams target owners too",
    body: "Voice cloning and flawless phishing emails mean 'the boss called and asked me to pay this invoice' is no longer proof of anything.",
    fix: "Agree a verification habit with your team for any unusual payment or data request — call back on a known number, or use a shared code word."
  },
];

$("#safetyArea").innerHTML = `
  <div class="rule-grid">
    ${safetyRules.map(r => `
      <div class="rule-card ${r.danger ? "danger" : ""}">
        <h3>${esc(r.title)}</h3>
        <p>${esc(r.body)}</p>
        <div class="fix">→ ${esc(r.fix)}</div>
      </div>`).join("")}
  </div>
  <div class="checklist">
    <h3>📄 Your one-page team AI policy — copy this</h3>
    <ul>
      <li>We encourage using AI for drafts, ideas, summaries, and admin — it's part of how we work now.</li>
      <li>Never enter customer personal data, payment details, health information, or passwords into AI tools.</li>
      <li>Everything customer-facing gets a human read before it's sent or published. No exceptions.</li>
      <li>Verify any fact, price, or claim the AI produces before using it.</li>
      <li>AI never makes decisions about people (hiring, discipline, refunds) — it only helps prepare them.</li>
      <li>Customer-facing bots are clearly labeled as automated, with an easy route to a human.</li>
      <li>Unusual payment or data requests get verified by phone on a known number, even if 'the boss' asked.</li>
      <li>If unsure whether something is OK to paste in — ask first.</li>
    </ul>
  </div>`;

/* ============================================================
   30-DAY PLAN
============================================================ */
const plan = [
  {
    week: "Week 1 — Get comfortable", sub: "Goal: AI is no longer intimidating. ~30 min/day.",
    items: [
      ["Create a free account", "on ChatGPT, Claude, or Gemini (see Tools & Costs to pick)."],
      ["Do the 'first session' walkthrough", "introduce your business, ask for a review reply, practice pushing back on drafts."],
      ["Ask the magic question", "\"What are 10 tasks in a business like mine you could help with weekly?\" Save the list."],
      ["Use AI for one real task on 3 different days", "an email, a summary, an idea list — anything real."],
      ["Read the Staying Safe rules", "and decide your personal line on what you'll never paste in."],
    ]
  },
  {
    week: "Week 2 — First real wins", sub: "Goal: two working habits that save visible time.",
    items: [
      ["Build your voice brief", "teach the AI your writing style from past examples; save the rules it produces."],
      ["Do the marketing email walkthrough", "draft, revise, fact-check, send a real email or post."],
      ["Do the review-mining walkthrough", "paste your reviews in and act on one insight."],
      ["Pick your 'anchor task'", "the one recurring task AI now always does first draft of. Put it in your calendar."],
      ["Track minutes saved this week", "roughly. You'll want this number in week 4."],
    ]
  },
  {
    week: "Week 3 — Involve the team & go deeper", sub: "Goal: it's not just you; AI touches a customer-facing process.",
    items: [
      ["Show one employee your wins", "20 minutes, live, on a real task. Curiosity spreads from demos, not memos."],
      ["Adopt the one-page AI policy", "from Staying Safe — edit it to fit, share it with the team."],
      ["Do the social-calendar walkthrough", "one sitting, a month of content scheduled."],
      ["Try a meeting notetaker", "on 2–3 calls (with consent) and forward the action lists."],
      ["List your top 20 customer questions", "this is the raw material for week 4's project."],
    ]
  },
  {
    week: "Week 4 — Automate one thing & decide", sub: "Goal: something runs without you, and you know your ROI.",
    items: [
      ["Do the chatbot OR automation walkthrough", "pick whichever matches your business; keep a human checkpoint on."],
      ["Run the ROI calculator", "with your real numbers from weeks 2–3 (see Tools & Costs)."],
      ["Decide your paid stack", "usually: one $20 assistant, maybe one specialist tool. Cancel anything that didn't earn it."],
      ["Write your 'next 90 days' list", "3 processes to bring AI into next, in order of annoyance."],
      ["Book a monthly 30-min 'AI review'", "recurring, in your calendar: what worked, what to try next. This habit is the whole game."],
    ]
  },
];

const PLAN_KEY = "aiadv-plan-v1";
let planState = {};
try { planState = JSON.parse(localStorage.getItem(PLAN_KEY) || "{}"); } catch { planState = {}; }

function renderPlan() {
  $("#planArea").innerHTML = plan.map((w, wi) => `
    <div class="week-block">
      <div class="week-head"><h3>${esc(w.week)}</h3><p>${esc(w.sub)}</p></div>
      <ul>
        ${w.items.map(([t, b], ii) => {
          const id = wi + "-" + ii;
          const done = !!planState[id];
          return `<li class="plan-item ${done ? "done" : ""}" data-plan="${id}">
            <input type="checkbox" ${done ? "checked" : ""} aria-label="${esc(t)}">
            <span><strong>${esc(t)}</strong> — ${esc(b)}</span>
          </li>`;
        }).join("")}
      </ul>
    </div>`).join("");
  updatePlanProgress();
}
function updatePlanProgress() {
  const total = plan.reduce((n, w) => n + w.items.length, 0);
  const done = Object.values(planState).filter(Boolean).length;
  const pct = Math.round((done / total) * 100);
  $("#planProgress").style.width = pct + "%";
  $("#planProgressLabel").textContent = pct + "% complete" + (pct === 100 ? " 🎉 — you run an AI-assisted business now" : "");
}
$("#planArea").addEventListener("click", (e) => {
  const item = e.target.closest("[data-plan]");
  if (!item) return;
  const id = item.dataset.plan;
  planState[id] = !planState[id];
  localStorage.setItem(PLAN_KEY, JSON.stringify(planState));
  renderPlan();
});
$("#planReset").addEventListener("click", () => {
  planState = {};
  localStorage.removeItem(PLAN_KEY);
  renderPlan();
  toast("Plan reset — fresh start");
});
renderPlan();

/* ============================================================
   QUIZ
============================================================ */
const quizQuestions = [
  {
    q: "How would you describe your relationship with technology?",
    opts: [
      ["I avoid it where I can", { cautious: 2 }],
      ["I use the basics fine — email, phone, maybe a website", { starter: 2 }],
      ["Comfortable — I pick up new apps easily", { builder: 2 }],
      ["I've already tried AI tools a bit", { builder: 1, automator: 2 }],
    ]
  },
  {
    q: "What eats the most of your week right now?",
    opts: [
      ["Writing things — marketing, emails, posts", { starter: 2, builder: 1 }],
      ["Answering the same customer questions over and over", { automator: 2 }],
      ["Paperwork — quotes, notes, documents, admin", { starter: 1, builder: 2 }],
      ["Honestly? Everything. I'm the bottleneck for it all", { cautious: 1, starter: 1, automator: 1 }],
    ]
  },
  {
    q: "How does most of your customer contact happen?",
    opts: [
      ["In person or on the phone", { cautious: 1, starter: 1 }],
      ["Email and messages", { starter: 1, builder: 1 }],
      ["Through my website / online store", { automator: 2 }],
      ["Social media", { starter: 2 }],
    ]
  },
  {
    q: "What's your honest feeling about AI?",
    opts: [
      ["Skeptical — feels like hype, and a bit risky", { cautious: 2 }],
      ["Curious but overwhelmed — where do I even start?", { starter: 2 }],
      ["Optimistic — I just need concrete direction", { builder: 2 }],
      ["Impatient — I want things automated yesterday", { automator: 2 }],
    ]
  },
  {
    q: "How much time can you invest in learning this per week?",
    opts: [
      ["An hour, maybe", { cautious: 1, starter: 1 }],
      ["2–3 hours", { starter: 1, builder: 1 }],
      ["Half a day — if it clearly pays back", { builder: 1, automator: 1 }],
      ["Whatever it takes to get time back long-term", { automator: 2 }],
    ]
  },
  {
    q: "Do you have anyone to help you with this?",
    opts: [
      ["Just me", { starter: 1 }],
      ["A tech-comfortable family member or employee", { builder: 1 }],
      ["A small team I could get on board", { builder: 1, automator: 1 }],
      ["I'd happily pay a freelancer for setup help", { automator: 1 }],
    ]
  },
];

const quizProfiles = {
  cautious: {
    title: "The Careful Observer",
    sub: "Skepticism is an asset — you'll adopt AI on your terms, with guardrails from day one.",
    first: "Read AI Basics (10 minutes), then do the 'first session' walkthrough using zero real business data — just play. Your first real task: paste in a long document you have to read anyway and ask for a summary. Private, low-stakes, immediately useful.",
    path: ["Read AI Basics + Staying Safe before anything else", "First session walkthrough with a free account, throwaway topics", "First real use: summarizing documents and drafting internal (never customer-facing) text", "Revisit in two weeks and only expand if it earned your trust"],
    tools: "One free assistant (ChatGPT, Claude, or Gemini). Spend nothing for at least a month."
  },
  starter: {
    title: "The Ready Starter",
    sub: "You're the ideal AI adopter: real workload, real writing tasks, and everything to gain from a first draft on tap.",
    first: "Do the 'first AI session' walkthrough today — 15 minutes. Then this week, the marketing email walkthrough: teach the AI your voice and send one real email or post it helped write.",
    path: ["First session walkthrough (today, 15 min)", "Marketing email walkthrough — build your voice brief", "Social calendar walkthrough — a month of content in one sitting", "Start the 30-Day Plan and let it pace you"],
    tools: "One free assistant for two weeks; upgrade to the ~$20 plan once you hit free limits — you will."
  },
  builder: {
    title: "The Momentum Builder",
    sub: "You'll move fast. Your risk isn't fear — it's spreading across ten tools instead of mastering one.",
    first: "Skip ahead: do the review-mining walkthrough this week (paste 50+ reviews in, act on one insight), and set up an AI meeting notetaker. Then pick ONE anchor task AI drafts every single week.",
    path: ["Review-mining walkthrough — insight from data you already own", "Meeting notetaker set up on this week's calls", "Quotes/proposals walkthrough if you send them; social calendar if you don't", "Week 4 of the 30-Day Plan: your first automation with a human checkpoint"],
    tools: "Paid assistant (~$20/mo) is worth it immediately for you. Add ONE specialist tool only after the assistant habit sticks."
  },
  automator: {
    title: "The Automation-Ready Owner",
    sub: "You have volume, digital channels, and appetite. The chatbot and automation walkthroughs were written for you.",
    first: "This week: list your top 20 customer questions, then follow the FAQ chatbot walkthrough end-to-end. Next week: the 'automate a task' walkthrough — review replies or inquiry triage are ideal first automations.",
    path: ["FAQ chatbot walkthrough — your top 20 questions, answered 24/7", "First automation with a human approval checkpoint", "Meeting notetaker + quote-from-voice-notes for your own time", "Monthly review habit: measure, keep, kill, expand"],
    tools: "Budget ~$50–100/mo: assistant + chatbot or connector tool (Zapier/Make). Run the ROI calculator — at your volume it pays for itself in days."
  },
};

let quizIdx = 0;
let quizScores = {};

function renderQuizQuestion() {
  const item = quizQuestions[quizIdx];
  $("#quizArea").innerHTML = `
    <div class="quiz-card">
      <div class="quiz-progress">Question ${quizIdx + 1} of ${quizQuestions.length}</div>
      <h2>${esc(item.q)}</h2>
      <div class="quiz-opts">
        ${item.opts.map((o, i) => `<button class="quiz-opt" data-opt="${i}">${esc(o[0])}</button>`).join("")}
      </div>
    </div>`;
  $$(".quiz-opt").forEach(btn => btn.addEventListener("click", () => {
    const weights = item.opts[+btn.dataset.opt][1];
    for (const k in weights) quizScores[k] = (quizScores[k] || 0) + weights[k];
    quizIdx++;
    if (quizIdx < quizQuestions.length) renderQuizQuestion();
    else renderQuizResult();
  }));
}

function renderQuizResult() {
  const winner = Object.entries(quizScores).sort((a, b) => b[1] - a[1])[0][0];
  const p = quizProfiles[winner];
  $("#quizArea").innerHTML = `
    <div class="quiz-card quiz-result">
      <div class="quiz-progress">Your starting point</div>
      <h2>${esc(p.title)}</h2>
      <p class="qr-sub">${esc(p.sub)}</p>
      <div class="qr-box"><h3>🎯 Do this first</h3><p>${esc(p.first)}</p></div>
      <div class="qr-box"><h3>🗺️ Your recommended path</h3><ol>${p.path.map(s => `<li>${esc(s)}</li>`).join("")}</ol></div>
      <div class="qr-box"><h3>💵 Tools & budget</h3><p>${esc(p.tools)}</p></div>
      <div class="hero-actions">
        <button class="btn btn-primary" data-nav="walkthroughs">Open the walkthroughs</button>
        <button class="btn btn-ghost" data-nav="plan">Start the 30-day plan</button>
        <button class="btn btn-ghost" id="quizRetake">Retake quiz</button>
      </div>
    </div>`;
  $("#quizRetake").addEventListener("click", () => { quizIdx = 0; quizScores = {}; renderQuizQuestion(); });
}
renderQuizQuestion();

/* Support / tips (Buy Me a Coffee) — change username after you create the page */
const BUY_ME_A_COFFEE_URL = "https://www.buymeacoffee.com/aiperth";
const BOOST_COMING_SOON_URL = "/boost-coming-soon.html";
const BILLING_LIVE = false; // flip true when Revolut Merchant is ready

function applySupportLinks() {
  document.querySelectorAll("#bmcFooterLink, #bmcBoosterLink, #bmcHomeLink").forEach((a) => {
    if (a) a.href = BUY_ME_A_COFFEE_URL;
  });
}
applySupportLinks();

/* ============================================================
   PROMPT BOOSTER
   Premium engine: Grok via xAI API (your Grok / xAI keys).
   Production: Cloudflare Pages Function proxies to
   https://api.x.ai/v1/chat/completions with XAI_API_KEY.
   Local demo: optional browser key (console.x.ai) if proxy is down.
============================================================ */
const BOOST_ENGINE = {
  name: "Grok on xAI",
  model: "grok-4.5",
  endpoint: "https://api.x.ai/v1/chat/completions",
  licenseStorage: "aiadv-boost-license",
  emailStorage: "aiadv-boost-email",
  costPerBoost: "$2/mo subscription",
  costPer1000: "unlimited while subscribed",
};

/* ---------- billing / license ($2 mo Revolut) ---------- */
function getBoostLicense() {
  return localStorage.getItem(BOOST_ENGINE.licenseStorage) || "";
}
function setBoostLicense(token) {
  if (token) localStorage.setItem(BOOST_ENGINE.licenseStorage, token);
  else localStorage.removeItem(BOOST_ENGINE.licenseStorage);
}
function getBillingEmail() {
  return localStorage.getItem(BOOST_ENGINE.emailStorage) || "";
}
function setBillingEmail(email) {
  if (email) localStorage.setItem(BOOST_ENGINE.emailStorage, email.toLowerCase().trim());
  else localStorage.removeItem(BOOST_ENGINE.emailStorage);
}

function setPaywallMsg(text, ok = false) {
  const el = $("#paywallMsg");
  if (!el) return;
  if (!text) {
    el.hidden = true;
    el.textContent = "";
    return;
  }
  el.hidden = false;
  el.textContent = text;
  el.classList.toggle("ok", !!ok);
}

function refreshPaywallUI(active) {
  const badge = $("#paywallBadge");
  const form = $("#paywallForm");
  const activeEl = $("#paywallActive");
  if (badge) {
    if (active) {
      badge.textContent = "Subscribed";
      badge.classList.add("active");
    } else if (!BILLING_LIVE) {
      badge.textContent = "Coming soon";
      badge.classList.remove("active");
    } else {
      badge.textContent = "Not subscribed";
      badge.classList.remove("active");
    }
  }
  if (form) form.hidden = !!active;
  if (activeEl) activeEl.hidden = !active;
  const emailInput = $("#billingEmail");
  if (emailInput && getBillingEmail() && !emailInput.value) {
    emailInput.value = getBillingEmail();
  }
}

async function claimLicense(email) {
  const res = await fetch("/api/billing/claim", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
    signal: AbortSignal.timeout(20000),
  });
  const data = await res.json().catch(() => ({}));
  if (data.active && data.license) {
    setBoostLicense(data.license);
    setBillingEmail(email);
    refreshPaywallUI(true);
    return data;
  }
  refreshPaywallUI(false);
  return data;
}

async function startCheckout(email) {
  if (!BILLING_LIVE) {
    window.location.href = BOOST_COMING_SOON_URL;
    return;
  }
  const res = await fetch("/api/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
    signal: AbortSignal.timeout(30000),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Checkout failed (${res.status})`);
  if (!data.checkoutUrl) throw new Error("No checkout URL returned from server.");
  setBillingEmail(email);
  window.location.href = data.checkoutUrl;
}

function wireBillingUI() {
  const emailInput = $("#billingEmail");
  if (emailInput && getBillingEmail()) emailInput.value = getBillingEmail();

  refreshPaywallUI(!!getBoostLicense());

  // After Revolut return: #/booster?billing=return&email=
  const hash = location.hash || "";
  const q = hash.includes("?") ? new URLSearchParams(hash.split("?")[1]) : new URLSearchParams();
  if (q.get("billing") === "return") {
    const email = (q.get("email") || getBillingEmail() || "").toLowerCase().trim();
    if (email) {
      setBillingEmail(email);
      if (emailInput) emailInput.value = email;
      setPaywallMsg("Confirming payment…");
      (async () => {
        let last = null;
        for (let i = 0; i < 8; i++) {
          last = await claimLicense(email);
          if (last.active) {
            setPaywallMsg("Subscription active — AI Boost unlocked.", true);
            toast("AI Boost unlocked");
            history.replaceState(null, "", "#/booster");
            return;
          }
          await new Promise((r) => setTimeout(r, 1500));
        }
        setPaywallMsg(last?.message || "Payment still processing — tap “I already paid” in a moment.");
      })().catch((e) => setPaywallMsg(e.message || "Could not confirm payment."));
    }
  }

  const subBtn = $("#subscribeBtn");
  if (subBtn) {
    if (!BILLING_LIVE && subBtn.tagName === "A") {
      subBtn.setAttribute("href", BOOST_COMING_SOON_URL);
    }
    subBtn.addEventListener("click", async (e) => {
      if (!BILLING_LIVE) {
        // allow normal navigation for <a>, or force redirect for <button>
        if (subBtn.tagName !== "A") {
          e.preventDefault();
          window.location.href = BOOST_COMING_SOON_URL;
        }
        return;
      }
      e.preventDefault();
      const email = ($("#billingEmail")?.value || "").trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setPaywallMsg("Enter a valid email first.");
        $("#billingEmail")?.focus();
        return;
      }
      subBtn.disabled = true;
      const orig = subBtn.textContent;
      subBtn.textContent = "Redirecting to Revolut…";
      setPaywallMsg("");
      try {
        await startCheckout(email);
      } catch (err) {
        setPaywallMsg(err.message || "Checkout failed.");
        subBtn.disabled = false;
        subBtn.textContent = orig;
      }
    });
  }

  $("#restoreSubBtn")?.addEventListener("click", async () => {
    const email = ($("#billingEmail")?.value || getBillingEmail() || "").trim().toLowerCase();
    if (!email) {
      setPaywallMsg("Enter the email you used to pay.");
      return;
    }
    setPaywallMsg("Looking up subscription…");
    try {
      const data = await claimLicense(email);
      if (data.active) {
        setPaywallMsg("Restored — AI Boost unlocked.", true);
        toast("Subscription restored");
      } else {
        setPaywallMsg(data.message || data.error || "No active subscription for that email.");
      }
    } catch (err) {
      setPaywallMsg(err.message || "Restore failed.");
    }
  });
}


/* ---------- option chips ---------- */
function chipValue(rowSel, attr) {
  const active = $(`${rowSel} .chip.active`);
  return active ? active.dataset[attr] : "";
}
$$("#toneChips .chip, #lengthChips .chip").forEach(chip => chip.addEventListener("click", () => {
  $$(".chip", chip.parentElement).forEach(c => c.classList.remove("active"));
  chip.classList.add("active");
}));

/* ---------- instant (free) boost: smart templates ---------- */
const boostRecipes = [
  { match: /review/i, role: "You are an experienced customer-service writer for a small business.",
    task: (idea) => `Write a public reply to a customer review. The situation, in my words: "${idea}". Thank them genuinely, address the specific issue without groveling or corporate clichés, mention one concrete thing we're doing about it, and invite them back.` },
  { match: /email|newsletter/i, role: "You are a marketing copywriter who specializes in small, local businesses and hates hype.",
    task: (idea) => `Write an email for my business. What I'm trying to do, in my words: "${idea}". One clear message, one clear call to action, and give me 3 subject-line options under 45 characters.` },
  { match: /social|post|instagram|facebook|caption|tiktok|reel/i, role: "You are a social media manager for a small business — practical, never cringe.",
    task: (idea) => `Write social media content. What I want, in my words: "${idea}". Make it sound like a real person, not a brand account. Include a suggested image idea I could photograph myself, and 3 relevant hashtags.` },
  { match: /\bad\b|advert|promo/i, role: "You are a direct-response copywriter for small businesses.",
    task: (idea) => `Write ad copy. The goal, in my words: "${idea}". Give me 3 variants with different angles (practical benefit, emotional, social proof) so I can test them.` },
  { match: /blog|article|website|web page|seo/i, role: "You are a content writer who makes small businesses findable on Google without keyword-stuffing.",
    task: (idea) => `Write website content. What I want, in my words: "${idea}". Plain English, short paragraphs, genuinely useful to the reader, with a title under 60 characters.` },
  { match: /quote|proposal|estimate/i, role: "You are an assistant who turns rough job notes into professional customer documents.",
    task: (idea) => `Draft a quote/proposal. The job, in my words: "${idea}". Structure: brief intro, scope as bullets, price section with [PRICE] placeholders, what's included and excluded. If any key detail is missing, add it to a QUESTIONS section instead of inventing it.` },
  { match: /letter|notice|policy|announce/i, role: "You are a business-communications writer who makes formal things sound human.",
    task: (idea) => `Draft this document. What it needs to say, in my words: "${idea}". Clear, respectful, no legalese, and flag anything I should have a professional check before sending.` },
  { match: /.*/, role: "You are a versatile assistant for a small-business owner.",
    task: (idea) => `Help me with this task, described in my words: "${idea}". Before answering, restate the task in one line so I can confirm you've understood it.` },
];

function instantBoost(idea, tone, length, audience) {
  const recipe = boostRecipes.find(r => r.match.test(idea));
  const lines = [
    recipe.role,
    "",
    `My business: [DESCRIBE YOUR BUSINESS IN ONE LINE — e.g. "family-run bakery in Perth, 8 staff"].`,
    "",
    recipe.task(idea.trim()),
    "",
    `Audience: ${audience ? audience : "[WHO THIS IS FOR]"}.`,
    `Tone: ${tone}.`,
    `Length: ${length}.`,
    "",
    "Rules: sound like a real person, not a corporation. No buzzwords, no exclamation-mark enthusiasm. If you need a fact you don't have (prices, dates, names), ask me instead of inventing it. Give me the draft, then one sentence on how to make it better.",
  ];
  return lines.join("\n");
}

/* ---------- premium (AI) boost: Grok via xAI ---------- */
function boosterMetaPrompt(idea, tone, length, audience) {
  return `You are an expert prompt engineer helping a non-technical small-business owner get great results from AI assistants (ChatGPT, Claude, Gemini).

Rewrite their rough idea into ONE excellent, ready-to-paste prompt.

Rules:
- Output ONLY the improved prompt text. No preamble, no explanation, no markdown fences, no quotes around it.
- Structure: a role line ("You are..."), the task stated precisely, the context the AI will need, then tone, length and audience requirements.
- Where the owner's real details are needed, insert [BRACKETED PLACEHOLDERS IN CAPS] rather than inventing specifics.
- End the prompt with an instruction to ask clarifying questions instead of inventing facts (prices, dates, names).
- Keep the finished prompt under 220 words.

Owner's rough idea: "${idea.trim()}"
Tone wanted: ${tone}
Length of the final output they want: ${length}
Audience: ${audience || "not specified — include a placeholder"}`;
}

async function aiBoost(idea, tone, length, audience) {
  const license = getBoostLicense();
  const headers = { "Content-Type": "application/json" };
  if (license) headers["Authorization"] = "Bearer " + license;
  const res = await fetch("/api/boost", {
    method: "POST",
    headers,
    signal: AbortSignal.timeout(30000),
    body: JSON.stringify({ idea, tone, length, audience, license: license || undefined }),
  });
  if (!res.ok) {
    let msg = `The AI service returned an error (${res.status}).`;
    let code = "";
    try {
      const err = await res.json();
      if (typeof err?.error === "string") msg = err.error;
      else if (err?.error?.message) msg = err.error.message;
      code = err?.code || "";
      if (res.status === 402 || code === "subscription_required") {
        refreshPaywallUI(false);
        msg = err?.error || "AI Boost is $2/month. Subscribe below to unlock.";
      } else if (res.status === 429) msg = "Rate limit reached — wait a moment and try again.";
      else if (res.status === 503) msg = err?.error || "AI Boost is not configured on the server yet.";
    } catch { /* keep default */ }
    const e = new Error(msg);
    e.code = code || (res.status === 402 ? "subscription_required" : "");
    throw e;
  }
  const data = await res.json();
  if (data?.error) throw new Error(typeof data.error === "string" ? data.error : "Boost failed.");
  const choice = data?.choices?.[0];
  const text = (data?.text || choice?.message?.content || "").trim();
  if (choice?.finish_reason === "length" || !text)
    throw new Error("The AI response was cut short — try again, or simplify your idea.");
  return text;
}

/* ---------- booster UI wiring ---------- */
let boostSeq = 0;   // guards against a slow AI response overwriting a newer boost

function showBoost(text, mode, foot, isError = false) {
  $("#boostEmpty").hidden = true;
  $("#boostOutput").hidden = false;
  const modeEl = $("#boostMode");
  modeEl.textContent = mode === "ai" ? "✨ AI boost — " + BOOST_ENGINE.name : "⚡ Instant boost";
  modeEl.classList.toggle("ai", mode === "ai");
  $("#boostText").textContent = text;
  $("#boostText").classList.toggle("booster-error", isError);
  $("#boostOutput [data-copy]").hidden = isError;
  $("#boostFoot").textContent = foot;
}

function boostInputs() {
  const idea = $("#boostIdea").value.trim();
  if (!idea) {
    $("#boostIdea").focus();
    toast("Type your rough idea first — a few words is enough");
    return null;
  }
  return {
    idea,
    tone: chipValue("#toneChips", "tone"),
    length: chipValue("#lengthChips", "length"),
    audience: $("#boostAudience").value.trim(),
  };
}

$("#boostLocalBtn").addEventListener("click", () => {
  const inp = boostInputs();
  if (!inp) return;
  boostSeq++;
  showBoost(
    instantBoost(inp.idea, inp.tone, inp.length, inp.audience),
    "local",
    "Built instantly from templates on this page — free, works offline. Fill in the [BRACKETS] and paste into any AI assistant. Want it custom-written around your exact wording? Try ✨ AI Boost ($2/mo)."
  );
});

$("#boostAiBtn").addEventListener("click", async () => {
  const inp = boostInputs();
  if (!inp) return;
  if (!getBoostLicense()) {
    if (!BILLING_LIVE) {
      window.location.href = BOOST_COMING_SOON_URL;
      return;
    }
    refreshPaywallUI(false);
    setPaywallMsg("Subscribe for $2/month to use AI Boost — or use Instant boost for free.");
    $("#billingEmail")?.focus();
    toast("AI Boost needs a $2/mo subscription");
    return;
  }
  const btn = $("#boostAiBtn");
  btn.disabled = true;
  const original = btn.innerHTML;
  btn.textContent = "✨ Boosting…";
  const seq = ++boostSeq;
  try {
    const text = await aiBoost(inp.idea, inp.tone, inp.length, inp.audience);
    if (seq !== boostSeq) return;
    showBoost(text, "ai",
      `Custom-written by ${BOOST_ENGINE.name}. Included in your $2/mo AI Boost subscription.`);
  } catch (err) {
    if (seq !== boostSeq) return;
    const msg = err.name === "TimeoutError"
      ? "The AI service took too long to respond — try again in a moment."
      : err.message;
    showBoost("⚠ " + msg + "\n\nMeanwhile, the ⚡ instant boost always works free.", "ai", "", true);
  } finally {
    btn.disabled = false;
    btn.innerHTML = original;
  }
});

wireBillingUI();


/* ---------- voice input: speak your idea instead of typing ----------
   Uses the browser's built-in Web Speech API (Chrome, Edge, Safari).
   No extra service, no extra cost; the mic button only appears when
   the browser supports it, so typing always remains the fallback. */
const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;

/* Merge freshly-delivered speech results into the running dictation state.
   Iterates only new results (from resultIndex) and skips the duplicate final
   segments that Android Chrome (confidence-0 re-delivery) and Safari
   (re-sent finals) are known to produce. Pure function — easy to test. */
function mergeSpeechResults(results, resultIndex, finalText) {
  let interim = "";
  for (let i = resultIndex; i < results.length; i++) {
    const r = results[i];
    const t = (r[0].transcript || "").replace(/\s+/g, " ").trim();
    if (!t) continue;
    if (r.isFinal) {
      if (r[0].confidence === 0) continue;      // Android Chrome duplicate re-delivery
      if (finalText.endsWith(t)) continue;      // Safari re-sent final
      finalText = finalText ? finalText + " " + t : t;
    } else {
      interim += (interim ? " " : "") + t;
    }
  }
  return { finalText, interim };
}

if (SpeechRec) {
  const micBtn = $("#micBtn");
  const ideaBox = $("#boostIdea");
  micBtn.hidden = false;
  let rec = null;           // active recognition session, or null
  let dictationBase = "";   // text already in the box when dictation started
  let finalText = "";       // confirmed speech accumulated this session
  let idleTimer = null;

  // Chrome auto-stops on silence but Safari/iOS never does — enforce the same
  // behavior everywhere so the mic can't stay hot after the user walks away.
  const armIdleStop = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => { try { if (rec) rec.stop(); } catch { /* already stopping */ } }, 9000);
  };

  const setListeningUI = (on) => {
    micBtn.classList.toggle("listening", on);
    micBtn.setAttribute("aria-pressed", String(on));
    micBtn.textContent = on ? "⏹" : "🎤";
    $("#micHint").hidden = !on;
    ideaBox.readOnly = on;    // dictation owns the box; typed edits would be overwritten
  };

  stopDictation = (target) => {
    if (rec && target !== "booster") { try { rec.stop(); } catch { /* already stopping */ } }
  };
  document.addEventListener("visibilitychange", () => { if (document.hidden) stopDictation(); });

  micBtn.addEventListener("click", () => {
    if (rec) { try { rec.stop(); } catch { /* already stopping */ } return; }

    rec = new SpeechRec();
    rec.lang = navigator.language || "en-US";
    rec.continuous = true;      // keep listening across sentences
    rec.interimResults = true;  // show words live while speaking
    dictationBase = ideaBox.value.trim();
    finalText = "";

    rec.onresult = (e) => {
      const merged = mergeSpeechResults(e.results, e.resultIndex, finalText);
      finalText = merged.finalText;
      ideaBox.value = [dictationBase, finalText, merged.interim].filter(Boolean).join(" ");
      armIdleStop();
    };
    rec.onerror = (e) => {
      const msgs = {
        "not-allowed": "Microphone blocked — click the mic or lock icon in Chrome's address bar and allow access, then try again.",
        "service-not-allowed": "Speech input isn't available here — type your idea instead.",
        "no-speech": "Didn't catch anything — tap the mic and try again, a bit closer to the microphone.",
        "audio-capture": "No microphone found — check one is plugged in and allowed.",
        "network": "Speech input needs an internet connection — check yours and retry.",
      };
      if (e.error !== "aborted") toast(msgs[e.error] || `Speech input hit a snag (${e.error}) — you can always just type.`);
    };
    rec.onend = () => {         // fires after stop(), any error, or the idle stop
      clearTimeout(idleTimer);
      rec = null;
      setListeningUI(false);
      const ae = document.activeElement;   // don't steal focus from another field
      if (!ae || ae === document.body || ae === micBtn) ideaBox.focus();
    };

    try {
      rec.start();
      setListeningUI(true);
      armIdleStop();
    } catch {
      rec = null;
      setListeningUI(false);
      toast("Couldn't start the microphone — try again.");
    }
  });
}


/* ---------- boot extras ---------- */
// Modules run after DOM parse; init once only (double-bind was breaking the hamburger)
initMobileNav();
